import type { ReactNode } from 'react';
import { Form, Submit, type SubmitHandler } from '@ff-it/form';
import { Button, DialogFooter } from '@ff-it/ui';

export type ProductFormShellProps<TValues extends Record<string, unknown>> = {
  initialValues: TValues;
  onSubmit: SubmitHandler<TValues>;
  validate?: (values: TValues) => Record<string, string>;
  /** Remounts the form when the edit target changes so initial values reapply. */
  resetKey: string | number;
  creating: boolean;
  onClose: () => void;
  /** Context-owned form fields rendered inside the form. */
  children: ReactNode;
  /** Extra footer actions between Close and Submit (e.g. catalogue actions). */
  footerActions?: ReactNode;
};

/**
 * Shared drawer-form shell: FinalForm wiring, footer, and submit labels common
 * to the outlet and price-list product drawers. Context wrappers own initial
 * values, validation, fields, and the submit adapter (endpoints or controller
 * commands).
 */
export function ProductFormShell<TValues extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  validate,
  resetKey,
  creating,
  onClose,
  children,
  footerActions,
}: ProductFormShellProps<TValues>) {
  return (
    <Form<TValues> key={resetKey} initialValues={initialValues} validate={validate} onSubmit={onSubmit} noValidate>
      {children}
      <DialogFooter mt="3">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {footerActions}
        <Submit variant="primary" ml="auto">
          {creating ? 'Create product' : 'Save product'}
        </Submit>
      </DialogFooter>
    </Form>
  );
}
